import React, { useCallback, useRef, useState } from "react";
import "../vehicles.css";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getVehicleByIdApi,
  getVehicleCategoryList,
  updateVehicleApi,
} from "services/customAPI";
import Loader from "components/loader/loader";
import Card from "components/card";
import { Button } from "@chakra-ui/react";
import Navbar from "components/navbar";
import pdf from "../../../../assets/svg/pdf.svg";
import { t } from "i18next";
import { DTO, Types } from "shared-lib";
import { errorToast, successToast } from "utils/commons";
import { FinalDocArray, profImage, VehicleFormLoggerProps } from "types/types";

const Logger = (props: VehicleFormLoggerProps): JSX.Element => {
  const { setIsDocuments } = props;
  const firstRender = useRef(true);
  const formik = useFormikContext<DTO.VehicleDTO>();
  const params = useParams();

  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      if (formik.values?.documentsKeyVehicle?.length > 0) {
        setIsDocuments(true);
      } else {
        if (params.id) {
          setIsDocuments(false);
        }
      }
    }
  }, [formik.values?.documentsKeyVehicle, params.id, setIsDocuments]);

  return null;
};

const VehicleForm: React.FC = () => {
  const [initialFormValues, setInitialFormValues] = useState<DTO.VehicleDTO>({
    vehicleName: "",
    vehicleNumber: "",
    vehicleType: "",
    vehicleMake: "",
    vehicleModel: "",
    color: "",
    year: "",
    vehicleDoc: "",
    profileImageKey: "",
    documentsKeyVehicle: [],
  });

  // Driver's vehicle type and transport mode info (read-only display)
  const [vehicleTypeInfo, setVehicleTypeInfo] = useState<{
    typeName?: string;
    typeCode?: string;
    transportModes?: string[];
    primaryTransportMode?: string;
  }>({});

  const [isLoading, setIsLoading] = useState(false);
  const anchorImageRef = useRef<HTMLAnchorElement>(null);
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const [approved, setApproved] = useState<Types.ApprovalStatus>(
    Types.ApprovalStatus.PENDING
  );
  const [finalDoc, setFinalDoc] = useState<FinalDocArray>(null);

  const [imagePreview, setImagePreview] = useState<string>("");
  const [initialDocArray, setInitialDocArray] = useState<string>("");
  const [listCategory, setListCategory] = useState<DTO.CategoryDTO[]>([]);
  const [finalProfileImage, setFinalProfileImage] = useState<profImage>(null);
  const [isdocuments, setIsDocuments] = useState(false);
  const isProfileImage = false;
  const FILE_SIZE_DOC = 1024 * 1024;
  const SUPPORTED_FORMATS_DOC = ["application/pdf"];
  const vehicleSchema = Yup.object().shape({
    vehicleNumber: Yup.string()
      .min(2, t("vehicleForm.validation.vehicle_name_required"))
      .required(t("vehicleForm.validation.vehicle_name_required")),
    color: Yup.string()
      .min(2, t("vehicleForm.validation.color_required"))
      .required(t("vehicleForm.validation.color_required")),
    year: Yup.string()
      .min(4, t("vehicleForm.validation.year_required"))
      .max(4, t("vehicleForm.validation.year_invalid"))
      .required(t("vehicleForm.validation.year_required")),
    vehicleDoc: Yup.string().required(
      t("vehicleForm.validation.vehicle_doc_required")
    ),
    vehicleMake: Yup.string()
      .min(2, t("vehicleForm.validation.vehicle_make_required"))
      .required(t("vehicleForm.validation.vehicle_make_required")),
    vehicleModel: Yup.string().required(
      t("vehicleForm.validation.vehicle_model_required")
    ),
    vehicleType: Yup.string().required(
      t("vehicleForm.validation.vehicle_type_required")
    ),
    image: isProfileImage ? Yup.mixed() : Yup.mixed(),
    documents: isdocuments
      ? Yup.array<File>()
        .test("fileSizeDoc", "File too large", (value: File[] | undefined) => {
          if (!value || value.length === 0) return true; // No new files, existing docs are fine
          let add = 0;
          for (let i = 0; i < value.length; i++) {
            add += value[i]?.size || 0;
          }
          return add <= FILE_SIZE_DOC;
        })
        .test("fileFormat", "Unsupported Format", (value: File[] | undefined) => {
          if (!value || value.length === 0) return true; // No new files, existing docs are fine
          return value.every((file) => SUPPORTED_FORMATS_DOC.includes(file?.type));
        })
      : Yup.mixed(),
  });
  const handleDelete = () => {
    setFinalDoc(null);
  };

  const handleCreateVehicle = async (values: DTO.VehicleDTO) => {
    setIsLoading(true);
    const formData = new FormData();

    try {
      formData.append("licensePlate", values.vehicleNumber);
      formData.append("vehicleName", values.vehicleName);
      formData.append("vehicleCategoryId", values.vehicleType);
      formData.append("vehicleMake", values.vehicleMake);
      formData.append("vehicleModel", values.vehicleModel);
      formData.append("color", values.color);
      formData.append("year", values.year);
      formData.append("licenseDoc", values.vehicleDoc);
      if (finalProfileImage?.file) {
        formData.append("profileImageKey", finalProfileImage?.file);
      }

      let res;
      if (params.id) {
        res = await updateVehicleApi(Number(params.id), formData);
      }
      if (res) {
        successToast(t("Veículo salvo com sucesso!"));
        navigate("/admin/vehicles");
        setIsLoading(false);
      } else {
        errorToast(t("Alguma coisa deu errado!"));
        setIsLoading(false);
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : t("Alguma coisa deu errado!");
      errorToast(errorMessage);
      setIsLoading(false);
    }
  };

  const getVehicleCategory = useCallback(async (vehicleTypeId?: number, transportModes?: string[]) => {
    try {
      const res = await getVehicleCategoryList({ vehicleTypeId, transportModes });
      const payload = res?.data ?? res;
      setListCategory(payload?.data ?? payload ?? []);
    } catch (error: unknown) {
      console.error('getVehicleCategory error:', error);
      setListCategory([]);
    }
  }, []);

  const BASE_IMAGE_URL = `${process.env.REACT_APP_BASE_URL}/uploads/`;
  const getData = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const res = await getVehicleByIdApi(id);
      const vehicle = res.data;
      setApproved(vehicle?.approved || Types.ApprovalStatus.PENDING);
      const profileImageUrl = vehicle?.profileImageKey;
      const key = vehicle?.profileImageKey;

      if (profileImageUrl) {
        const imageUrl = `${BASE_IMAGE_URL}${profileImageUrl}`;
        setFinalProfileImage({ key, url: imageUrl });
        setImagePreview(imageUrl);
      }

      if (vehicle) {
        setInitialFormValues({
          ...vehicle,
          vehicleType: vehicle.vehicleCategory?.id ? String(vehicle.vehicleCategory.id) : "",
        });

        setInitialDocArray(vehicle.documentsKeyVehicle?.[0] || "");

        // Store driver's vehicle type and transport mode info for display
        const vt: any = vehicle.vehicleType;
        const vTypeId = vehicle.vehicleTypeId || (vt?.id ? vt.id : undefined);
        const vtCode = vt?.type || vt;
        const vtName = vt?.label || (typeof vtCode === 'string' ? Types.getVehicleTypeLabel(vtCode) : '');
        const vTransportModes = vehicle.transportModes ?? [];
        setVehicleTypeInfo({
          typeName: vtName,
          typeCode: typeof vtCode === 'string' ? vtCode : '',
          transportModes: vTransportModes,
          primaryTransportMode: vehicle.primaryTransportMode ?? '',
        });

        // Fetch categories filtered by vehicle type AND transport modes
        getVehicleCategory(vTypeId, vTransportModes.length > 0 ? vTransportModes : undefined);
      }
      setIsLoading(false);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : t("Alguma coisa deu errado!");
      errorToast(errorMessage);
      setIsLoading(false);
    }
  }, [BASE_IMAGE_URL, getVehicleCategory]);
  const handleDivClickImg = () => {
    if (anchorImageRef.current) {
      anchorImageRef.current.click();
    }
  };

  const handleOpenDoc = (url: string) => {
    window.open(url, "_blank");
  };

  const handleClick = () => {
    setInitialFormValues({
      ...initialFormValues,
      vehicleName: params.id
        ? initialFormValues.vehicleName
        : initialFormValues.vehicleNumber,
    });
  };
  const handleApprove = async (id: number) => {
    try {
      const driverApproved = await updateVehicleApi(id, {
        approved: Types.ApprovalStatus.APPROVED,
      });
      const res = await getVehicleByIdApi(id);
      const vehicle = res.data;
      setApproved(vehicle?.approved || Types.ApprovalStatus.PENDING);
      toast.success(t("Driver approved successfully"));
      return driverApproved;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : t("Alguma coisa deu errado!");
      console.error("error", errorMessage);
      toast.error(t(errorMessage));
      throw error;
    }
  };

  const handleClickForRejectDoc = async (id: number) => {
    try {
      const driverRejected = await updateVehicleApi(id, {
        approved: Types.ApprovalStatus.REJECTED,
      });
      const res = await getVehicleByIdApi(id);
      const vehicle = res.data;
      setApproved(vehicle?.approved || Types.ApprovalStatus.PENDING);
      toast.success(t("Driver rejected successfully"));
      return driverRejected;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : t("Alguma coisa deu errado!");
      console.error("error", errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  };


  React.useEffect(() => {
    const fetchData = async () => {
      if (params.id) {
        await getData(Number(params.id));
      } else {
        await getVehicleCategory();
      }
    };
    fetchData();
  }, [getData, getVehicleCategory, params.id]);

  return (
    <>
      <Navbar flag={false} brandText={t("vehicleform")} />
      {isLoading ? (
        <Loader />
      ) : (
        <Card extra={"w-full pb-6 p-4 mt-4 pt-10"}>
          <header className="relative flex items-center justify-between ps-10">
            {params.id ? (
              <div className="text-xl font-bold text-navy-700 dark:text-white">
                {t("Edit Vehicle")}
              </div>
            ) : (
              <div className="text-xl font-bold text-navy-700 dark:text-white">
                {t("Add Vehicle")}
              </div>
            )}
          </header>
          <div className="p-6 pb-5 pe-6 ps-6" onClick={handleClick}>
            <Formik
              enableReinitialize={true}
              initialValues={initialFormValues}
              onSubmit={(values) => {
                handleCreateVehicle(values);
              }}
              validationSchema={vehicleSchema}
            >
              {({
                handleChange,
                setFieldValue,
                handleBlur,
                handleSubmit,
                values,
                errors,
                touched,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Logger
                    display="hidden"
                    allAvailableVehiclesTypes={listCategory}
                    setIsDocuments={setIsDocuments}
                  />
                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label
                        htmlFor="vehicleNumber"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Vehicle Number")}
                      </label>
                      <input
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        required
                        name="vehicleNumber"
                        type="text"
                        id="vehicleNumber"
                        width="90%"
                        placeholder={t("Vehicle Number")}
                        onChange={handleChange}
                        onBlur={(event) => {
                          handleBlur(event);
                          if (!params.id) {
                            setFieldValue("vehicleNumber", event.target.value);
                          }
                        }}
                        value={values?.vehicleNumber}
                        aria-describedby="exampleFormControlInputHelpInline"
                      />
                      {errors.vehicleNumber && touched.vehicleNumber ? (
                        <div className="error-input">
                          {errors.vehicleNumber}
                        </div>
                      ) : null}
                    </div>
                    <div className="mb-3 ms-6 w-full">
                      <label
                        htmlFor="vehicleName"
                        className="input-custom-label dark:text-white"
                      >
                        {t("VehicleLicenseDoc")}
                      </label>
                      <input
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        required
                        name="vehicleDoc"
                        type="text"
                        id="vehicleDoc"
                        width="90%"
                        placeholder={t("VehicleLicenseDoc")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.vehicleDoc}
                        aria-describedby="exampleFormControlInputHelpInline"
                      />
                      {errors.vehicleDoc && touched.vehicleDoc ? (
                        <div className="error-input">{errors.vehicleDoc}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label
                        htmlFor="vehicleModel"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Vehicle Model")}
                      </label>
                      <input
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        required
                        name="vehicleModel"
                        type="text"
                        id="vehicleModel"
                        width="90%"
                        placeholder={t("Vehicle Model")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.vehicleModel}
                        aria-describedby="exampleFormControlInputHelpInline"
                      />
                      {errors.vehicleModel && touched.vehicleModel ? (
                        <div className="error-input">{errors.vehicleModel}</div>
                      ) : null}
                    </div>
                    <div className="mb-3 ms-6 w-full">
                      <label
                        htmlFor="vehicleMake"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Vehicle Make")}
                      </label>
                      <input
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="vehicleMake"
                        type="text"
                        id="vehicleMake"
                        width="90%"
                        placeholder={t("Vehicle Make")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.vehicleMake}
                      />
                      {errors.vehicleMake && touched.vehicleMake ? (
                        <div className="error-input">{errors.vehicleMake}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label
                        htmlFor="color"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Color")}
                      </label>
                      <input
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        required
                        name="color"
                        type="text"
                        id="color"
                        placeholder={t("Color")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.color}
                      />
                      {errors.color && touched.color ? (
                        <div className="error-input">{errors.color}</div>
                      ) : null}
                    </div>
                    <div className="mb-3 ms-6 w-full">
                      <label
                        htmlFor="year"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Year")}
                      </label>
                      <input
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        required
                        name="year"
                        type="text"
                        id="year"
                        placeholder={t("Year")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.year}
                      />
                      {errors.year && touched.year ? (
                        <div className="error-input">{errors.year}</div>
                      ) : null}
                    </div>
                  </div>

                  {/* Driver's Vehicle Type & Transport Modes (read-only) */}
                  {params.id && (vehicleTypeInfo.typeName || (vehicleTypeInfo.transportModes?.length ?? 0) > 0) && (
                    <div className="mb-4 me-6 w-full rounded-xl border border-white/10 bg-white/[0.03] p-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {t("Vehicle Info (Driver)")}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {vehicleTypeInfo.typeName && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 px-3 py-1 text-sm font-medium text-cyan-400">
                            {vehicleTypeInfo.typeCode && Types.getVehicleTypeEmoji(vehicleTypeInfo.typeCode)}{' '}
                            {vehicleTypeInfo.typeName}
                          </span>
                        )}
                        {vehicleTypeInfo.transportModes?.map((mode) => {
                          const isPrimary = mode === vehicleTypeInfo.primaryTransportMode;
                          return (
                            <span
                              key={mode}
                              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium border ${isPrimary
                                ? 'bg-green-500/15 border-green-500/30 text-green-400'
                                : 'bg-white/5 border-white/10 text-gray-400'
                                }`}
                            >
                              {Types.getTransportModeEmoji(mode)} {Types.getTransportModeLabel(mode)}
                              {isPrimary && (
                                <span className="text-[10px] font-bold uppercase ml-1 text-green-400">({t("Primary")})</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Category dropdown (filtered by vehicle type + transport modes) */}
                  <div className="mb-3 me-6 w-full">
                    <label
                      htmlFor="vehicleType"
                      className="input-custom-label dark:text-white"
                    >
                      {t("Category")}
                    </label>
                    <select
                      className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                      required
                      name="vehicleType"
                      id="vehicleType"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.vehicleType}
                    >
                      <option value="">
                        {t("Select Category")}
                      </option>
                      {listCategory.map((category: DTO.CategoryDTO) => (
                        <option key={category.id} value={String(category.id)}>
                          {Types.getTransportModeEmoji(category.transportMode ?? '')} {category.name}
                        </option>
                      ))}
                    </select>

                    {listCategory.length === 0 && vehicleTypeInfo.typeName && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: '10px 14px',
                          borderRadius: 8,
                          backgroundColor: 'rgba(251, 191, 36, 0.12)',
                          border: '1px solid rgba(251, 191, 36, 0.3)',
                          fontSize: 13,
                          color: '#fbbf24',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span>⚠️</span>
                        <span>
                          {t("Nenhuma categoria encontrada para o tipo")} <strong>{vehicleTypeInfo.typeName}</strong>.
                        </span>
                        <a
                          href="/admin/settings/categories"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate('/admin/settings/categories');
                          }}
                          style={{
                            color: '#60a5fa',
                            textDecoration: 'underline',
                            fontWeight: 500,
                            cursor: 'pointer',
                          }}
                        >
                          {t("Criar categoria")} →
                        </a>
                      </div>
                    )}

                    {errors.vehicleType && touched.vehicleType ? (
                      <div className="error-input">{errors.vehicleType}</div>
                    ) : null}
                  </div>
                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label className="input-custom-label dark:text-white">
                        {t("vehicleForm.labels.vehicle_image")}
                      </label>
                      <div className="mt-4">
                        {imagePreview && (
                          <>
                            <div
                              className="image-preview"
                              style={{
                                width: "55px",
                                height: "55px",
                                padding: "2px",
                                border: "2px solid #9CA3AF",
                                borderRadius: "4px",
                              }}
                            >
                              <img
                                src={imagePreview}
                                style={{
                                  objectFit: "contain",
                                  height: "100%",
                                  width: "auto",
                                  cursor: "pointer",
                                  padding: "5px",
                                }}
                                alt="img"
                                onClick={handleDivClickImg}
                              />
                              <a
                                ref={anchorImageRef}
                                href={imagePreview}
                                download="your-image-file.png"
                                style={{ display: "none" }}
                                aria-label={t("Baixar imagem")}
                              >
                                {t("Baixar imagem")}
                              </a>
                            </div>
                          </>
                        )}
                        <ErrorMessage
                          name="image"
                          component="div"
                          className="error-input"
                        />
                      </div>
                      <div className="mt-4">
                        <label className="input-custom-label dark:text-white">
                          {t("Status")}
                        </label>
                        <div>
                          {approved === Types.ApprovalStatus.PENDING && (
                            <span className="font-bold text-yellow-500">
                              {t("Pending")}
                            </span>
                          )}
                          {approved === Types.ApprovalStatus.REJECTED && (
                            <span className="font-bold text-red-500">
                              {t("Rejected")}
                            </span>
                          )}
                          {approved === Types.ApprovalStatus.APPROVED && (
                            <span className="font-bold text-green-500">
                              {t("Approved")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* docs */}
                    <div className="mb-3 ms-6 w-full">
                      <label className="input-custom-label dark:text-white">
                        {t("Vehicle Document")}
                      </label>
                      <div className="flex flex-row items-start">
                        <>
                          <div
                            className="document-container m-0 mb-2 me-2"
                            key={finalDoc?.key}
                          >
                            <div
                              style={{
                                position: "relative",
                                width: "65px",
                                height: "65px",
                                padding: "2px",
                                cursor: "pointer",
                                border: "2px solid #9CA3AF",
                                borderRadius: "4px",
                              }}
                            >
                              <img
                                src={pdf}
                                style={{
                                  objectFit: "contain",
                                  height: "100%",
                                  width: "auto",
                                  cursor: "pointer",
                                  padding: "5px",
                                }}
                                alt={t("Documento")}
                                onClick={() =>
                                  handleOpenDoc(
                                    finalDoc?.url ||
                                    `${BASE_IMAGE_URL}${initialDocArray}`
                                  )
                                }
                              />
                              <p>{finalDoc?.key.slice(0, 6)}</p>
                              <span
                                style={{
                                  position: "absolute",
                                  top: "0",
                                  right: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  padding: "-2px -2px 5px 5px",
                                }}
                                onClick={() => {
                                  handleDelete();
                                }}
                              ></span>
                            </div>
                          </div>
                        </>
                      </div>
                      <ErrorMessage
                        name="documents"
                        component="div"
                        className="error-input"
                      />
                    </div>
                  </div>
                  <div className="button-save-cancel mt-[50px] flex justify-end">
                    <div className="flex items-center">
                      <Button
                        color="dark"
                        className="cancel-button my-2 ms-1 sm:my-0"
                        onClick={() => navigate("/admin/vehicles")}
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        color="dark"
                        type="submit"
                        className={`save-button bg-primary my-2 mr-5 ms-1 dark:bg-brand-400 sm:my-0`}
                        onClick={() => handleSubmit()}
                      >
                        {t("Save")}
                      </Button>
                      {approved === Types.ApprovalStatus.PENDING && (
                        <div className="flex items-center">
                          <Button
                            className="approve-modal-button mx-2"
                            onClick={() => {
                              if (params.id) {
                                handleApprove(Number(params.id));
                              }
                            }}
                          >
                            {t("approve")}
                          </Button>
                          <Button
                            className="delete-modal-button  mx-2"
                            onClick={() => {
                              if (params.id) {
                                handleClickForRejectDoc(Number(params.id));
                              }
                            }}
                          >
                            {t("reject")}
                          </Button>
                        </div>
                      )}
                      {approved === Types.ApprovalStatus.REJECTED && (
                        <div className="flex items-center">
                          <Button
                            className="approve-modal-button mx-2"
                            onClick={() => {
                              if (params.id) {
                                handleApprove(Number(params.id));
                              }
                            }}
                          >
                            {t("approve")}
                          </Button>
                        </div>
                      )}
                      {approved === Types.ApprovalStatus.APPROVED && (
                        <div className="flex items-center">
                          <Button
                            className="delete-modal-button mx-2"
                            onClick={() => {
                              if (params.id) {
                                handleClickForRejectDoc(Number(params.id));
                              }
                            }}
                          >
                            {t("reject")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </Card>
      )}
    </>
  );
};
export default VehicleForm;
